import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Filter, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { id: "", label: "Todas etapas" },
  { id: "novo", label: "Novo" },
  { id: "em_atendimento", label: "Em atendimento" },
  { id: "qualificado", label: "Qualificado" },
  { id: "visita", label: "Visita" },
  { id: "proposta", label: "Proposta" },
  { id: "fechamento", label: "Fechamento" },
  { id: "ganho", label: "Ganho" },
  { id: "fechado", label: "Fechado" },
  { id: "perdido", label: "Perdido" },
];

const STAGE_COLORS = {
  novo: "bg-white/8 text-white",
  em_atendimento: "bg-cyan-500/15 text-cyan-300",
  qualificado: "bg-[#3B9EFF]/15 text-[#7FBEFF]",
  visita: "bg-emerald-500/15 text-emerald-300",
  proposta: "bg-amber-500/15 text-amber-300",
  fechamento: "bg-lime-500/15 text-lime-300",
  ganho: "bg-green-500/20 text-green-300",
  fechado: "bg-green-500/15 text-green-300",
  perdido: "bg-red-500/15 text-red-300",
};

export default function AgencyLeads() {
  const [params, setParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [realtors, setRealtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const realtor_id = params.get("realtor_id") || "";
  const stage = params.get("stage") || "";

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (realtor_id) q.set("realtor_id", realtor_id);
    if (stage) q.set("stage", stage);
    api.get(`/agency/leads?${q.toString()}`).then((r) => setLeads(r.data.leads)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [realtor_id, stage]);
  useEffect(() => { api.get("/agency/realtors").then((r) => setRealtors(r.data.realtors)); }, []);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const removeLead = async (lead) => {
    const ok = window.confirm(`Excluir o lead ${lead.name}?\n\nCorretor: ${lead.realtor_name}\nEsta ação é definitiva e não pode ser desfeita.`);
    if (!ok) return;
    try {
      await api.delete(`/agency/leads/${lead.id}`);
      toast.success(`Lead ${lead.name} excluído`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir lead");
    }
  };

  const filtered = leads.filter((l) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.name || "").toLowerCase().includes(s) || (l.neighborhood || "").toLowerCase().includes(s) || (l.realtor_name || "").toLowerCase().includes(s);
  });

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="agency-leads">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-[#3B9EFF] font-bold">Todos os leads</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-extrabold mt-1">Carteira consolidada</h1>
          <p className="text-white/60 text-sm mt-2">{leads.length} leads no total. Filtre por corretor ou etapa.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="lead-search-input"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, bairro ou corretor..."
            className="w-full pl-9 pr-3 py-2.5 bg-[#0F1F3D] border border-white/10 rounded-lg text-white placeholder:text-white/40 text-sm focus:outline-none focus:border-[#3B9EFF]/50"
          />
        </div>
        <select data-testid="realtor-filter" value={realtor_id} onChange={(e) => setFilter("realtor_id", e.target.value)}
          className="px-3 py-2.5 bg-[#0F1F3D] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#3B9EFF]/50">
          <option value="">Todos os corretores</option>
          {realtors.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select data-testid="stage-filter" value={stage} onChange={(e) => setFilter("stage", e.target.value)}
          className="px-3 py-2.5 bg-[#0F1F3D] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#3B9EFF]/50">
          {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="p-8 text-white/60">Carregando leads...</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center bg-[#0F1F3D] border border-dashed border-white/15 rounded-2xl">
          <div className="text-white/60">Nenhum lead encontrado com esses filtros.</div>
        </div>
      ) : (
        <div className="bg-[#0F1F3D] border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-white/40 font-bold border-b border-white/10">
                  <th className="px-4 py-3">Lead</th>
                  <th className="px-4 py-3">Corretor</th>
                  <th className="px-4 py-3">Etapa</th>
                  <th className="px-4 py-3">Bairro</th>
                  <th className="px-4 py-3">Orçamento</th>
                  <th className="px-4 py-3">Criado</th>
                  <th className="px-4 py-3 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 hover:bg-white/4 transition" data-testid={`agency-lead-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-white">{l.name}</div>
                      <div className="text-xs text-white/50">{l.phone || "-"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-[#3B9EFF]/20 text-[#7FBEFF] flex items-center justify-center text-xs font-bold">
                          {l.realtor_name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <span className="text-white/80 text-xs">{l.realtor_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-[11px] font-semibold ${STAGE_COLORS[l.stage] || STAGE_COLORS.novo}`}>
                        {STAGES.find(s => s.id === l.stage)?.label || l.stage}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/70">{l.neighborhood || "-"}</td>
                    <td className="px-4 py-3 text-white/70">{l.budget ? `R$ ${Number(l.budget).toLocaleString('pt-BR')}` : "-"}</td>
                    <td className="px-4 py-3 text-white/50 text-xs">{l.created_at ? new Date(l.created_at).toLocaleDateString('pt-BR') : "-"}</td>
                    <td className="px-2 py-3">
                      <button onClick={() => removeLead(l)} data-testid={`delete-lead-${l.id}`}
                        className="p-1.5 rounded hover:bg-red-500/15 text-white/40 hover:text-red-400 transition"
                        title="Excluir lead">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
